import axios from 'axios';

// API base URL from environment or default - use relative URL in development
const API_URL = '';

// Create axios instance
const api = axios.create({
  baseURL: API_URL
});

// Payment processors config
const paymentProcessors = {
  stripe: {
    name: 'Stripe',
    logo: 'https://stripe.com/img/v3/home/twitter.png'
  },
  paypal: {
    name: 'PayPal',
    logo: 'https://www.paypalobjects.com/webstatic/mktg/logo/pp_cc_mark_37x23.jpg'
  },
  square: {
    name: 'Square',
    logo: 'https://cdn.cms-twdigitalassets.com/content/dam/help-twitter/twitter-logo.png.twimg.1920.png'
  }
};

// Get clinic info by ID or stub
export const getClinicInfo = async (clinicId, clinicStub) => {
  try {
    let params = {};
    if (clinicId) {
      params.id = clinicId;
    } else if (clinicStub) {
      params.stub = clinicStub;
    } else {
      throw new Error('Either clinicId or clinicStub must be provided');
    }
    
    const response = await api.get('/api/clinics', { params });
    return response.data;
  } catch (error) {
    console.error('Error fetching clinic info:', error);
    throw error;
  }
};

// Get services (appointment types) for a clinic
export const getClinicServices = async (clinicId) => {
  try {
    console.log(`Fetching services for clinic ID: ${clinicId}`);
    
    const response = await api.get(`/api/appointment-types/clinic/${clinicId}`);
    console.log(`Retrieved ${response.data.length} appointment types from API`);
    
    // Log details about the appointment types
    response.data.forEach((service, index) => {
      console.log(`Service ${index + 1}: ${service.name}, enabled: ${service.is_enabled}, price: ${service.price}`);
    });
    
    // Only return enabled appointment types
    const enabledServices = response.data.filter(service => service.is_enabled === true);
    console.log(`Filtered to ${enabledServices.length} enabled appointment types`);
    
    // Sort by sort_order and then name
    const sortedServices = enabledServices.sort((a, b) => {
      if (a.sort_order !== b.sort_order) {
        return a.sort_order - b.sort_order;
      }
      return a.name.localeCompare(b.name);
    });
    
    return sortedServices;
  } catch (error) {
    console.error('Error fetching clinic services:', error);
    throw error;
  }
};

// Get practitioners for a clinic, optionally filtered by appointment type
export const getPractitioners = async (clinicId, appointmentTypeId = null) => {
  try {
    const params = { clinic_id: clinicId };
    
    // Add appointment type filter if provided
    if (appointmentTypeId) {
      params.appointment_type_id = appointmentTypeId;
      console.log(`Filtering practitioners for appointment type ID: ${appointmentTypeId}`);
    }
    
    const response = await api.get(`/api/practitioners`, { params });
    
    // Log results for debugging
    if (response.data && response.data.practitioners) {
      console.log(`Retrieved ${response.data.practitioners.length} practitioners ${appointmentTypeId ? 'for service' : ''}`);
    }
    
    return response.data;
  } catch (error) {
    console.error('Error fetching practitioners:', error);
    throw error;
  }
};

// Get available times for appointment
export const getAvailableTimes = async (params) => {
  try {
    console.log('Fetching available times with params:', params);
    const response = await api.get('/api/available-times', { params });
    
    // Enhanced logging of response
    console.log('Available times response status:', response.status);
    
    if (response.data && response.data.available_times) {
      const count = response.data.available_times.length;
      console.log(`Available times response contains ${count} time slots`);
      
      // Log a sample of times if available
      if (count > 0) {
        console.log('Sample time:', response.data.available_times[0]);
      }
    } else {
      console.log('Response data structure:', Object.keys(response.data || {}));
      console.log('No available_times array in response or empty array');
    }
    
    // Ensure we always return an object with available_times array
    if (!response.data) {
      console.warn('No data in response, returning empty array');
      return { available_times: [] };
    }
    
    if (!response.data.available_times) {
      console.warn('No available_times property in response, creating empty array');
      return { ...response.data, available_times: [] };
    }
    
    return response.data;
  } catch (error) {
    console.error('Error fetching available times:', error);
    // Return empty array rather than throwing
    return { available_times: [], error: error.message };
  }
};

// Create a booking
export const createBooking = async (bookingData) => {
  try {
    // Save the original appointment details for logging
    const logDetails = {
      appointmentTime: bookingData.starts_at,
      patient: `${bookingData.patient_first_name} ${bookingData.patient_last_name}`,
      clinic: bookingData.clinic_id
    };
    console.log('Creating booking with data:', logDetails);
    
    // TIMEZONE FIX - Check if we have an existing time in sessionStorage 
    // that was previously shown to the user
    const storedTime = sessionStorage.getItem('appointment_time_display');
    
    // Check for pending request to prevent duplicates 
    const pendingBookingRequestTime = sessionStorage.getItem('pending_booking_request_time');
    
    if (pendingBookingRequestTime) {
      const lastAttemptTime = parseInt(pendingBookingRequestTime, 10);
      const now = Date.now();
      const timeSinceLastAttempt = now - lastAttemptTime;
      
      // If last attempt was less than 5 seconds ago, block as potential duplicate
      if (timeSinceLastAttempt < 5000) {
        console.warn(`Blocking potential duplicate booking request (last attempt: ${timeSinceLastAttempt}ms ago)`);
        throw new Error('Please wait a moment before trying again.');
      }
    }
    
    // Set pending request time
    sessionStorage.setItem('pending_booking_request_time', Date.now().toString());
    
    // Generate a unique request ID to prevent duplicate submissions
    // This helps avoid 409 conflicts by ensuring we don't submit the same booking twice
    const requestId = `booking_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    bookingData.request_id = requestId;
    
    // Add the request ID to a queue in localStorage to check for duplicates
    let processedIds = [];
    try {
      const storedIds = sessionStorage.getItem('processed_booking_request_ids');
      if (storedIds) {
        processedIds = JSON.parse(storedIds);
      }
    } catch (e) {
      console.error('Error parsing stored request IDs:', e);
    }
    
    // Check if this ID is in our recently processed list
    if (processedIds.includes(requestId)) {
      console.warn('Preventing duplicate booking submission with same request ID');
      throw new Error('Duplicate booking submission prevented');
    }
    
    // Add this ID to the processed list (limited to last 10)
    processedIds.push(requestId);
    if (processedIds.length > 10) {
      processedIds = processedIds.slice(-10);
    }
    
    // Store the updated list
    sessionStorage.setItem('processed_booking_request_ids', JSON.stringify(processedIds));
    
    // Make the API call, with better 409 error handling
    try {
      const response = await api.post('/api/appointments', bookingData);
      
      // Clear the pending flag on success
      sessionStorage.removeItem('pending_booking_request_time');
      
      // Mark this as a successful booking to help with duplicate request handling
      sessionStorage.setItem('duplicate_booking_success', 'true');
      
      // Extract the correct display time from the response if available
      try {
        // ALWAYS store the original raw ISO time for reconstruction if needed
        const rawTime = bookingData.starts_at;
        if (rawTime) {
          sessionStorage.setItem('raw_appointment_time', rawTime);
          console.log('Stored raw appointment time in sessionStorage:', rawTime);
        }
        
        // SIMPLE FIX: First check if we already have the user's selected time display
        const userSelectedTime = sessionStorage.getItem('user_selected_time_display');
        if (userSelectedTime) {
          console.log('Using user selected time:', userSelectedTime);
          sessionStorage.setItem('appointment_time_display', userSelectedTime);
          return response.data;
        }

        // FIXED TIMESTAMP HANDLING: Use exact time with no adjustment for post-booking confirmation
        try {
          const date = new Date(bookingData.starts_at);
          
          // For the confirmation page, we need NO adjustment (the time should match Cliniko)
          // We're removing the -30 minute adjustment completely
          const adjustedTime = new Date(date);
          
          // Format with consistent format - ALWAYS use Melbourne timezone
          const formattedTime = adjustedTime.toLocaleString('en-AU', {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: 'numeric',
            minute: 'numeric',
            hour12: true,
            timeZone: 'Australia/Melbourne'
          });
          
          sessionStorage.setItem('appointment_time_display', formattedTime);
          console.log('Created and stored exact appointment time (no adjustment):', formattedTime);
        } catch (timeAdjustError) {
          console.error('Error applying time adjustment:', timeAdjustError);
          
          // Fallbacks if the adjustment fails
          if (response.data && response.data.appointment_display_time) {
            const displayTime = response.data.appointment_display_time;
            console.log('Using backend display time as fallback:', displayTime);
            
            if (displayTime.formatted) {
              sessionStorage.setItem('appointment_time_display', displayTime.time_display || displayTime.formatted);
            }
          } else if (storedTime) {
            // Keep using the existing stored time if available
            console.log('Using existing stored appointment time as fallback:', storedTime);
          } else {
            // Original fallback formatting
            console.log('Using original fallback time formatting');
            const formattedTime = new Date(bookingData.starts_at).toLocaleString('en-AU', {
              weekday: 'long',
              year: 'numeric',
              month: 'long',
              day: 'numeric',
              hour: 'numeric',
              minute: 'numeric',
              hour12: true,
              timeZone: 'Australia/Melbourne'
            });
            
            sessionStorage.setItem('appointment_time_display', formattedTime);
          }
        }
      } catch (storageError) {
        console.error('Error storing appointment time:', storageError);
      }
      
      return response.data;
    } catch (error) {
      // Enhanced error handling, particularly for 409 Conflict errors
      console.error('Error creating booking:', error);
      
      // Clear pending request on error
      sessionStorage.removeItem('pending_booking_request_time');
      
      // Special handling for specific error types
      if (error.response && error.response.status === 409) {
        // 409 Conflict errors
        const errorData = error.response.data || {};
        
        // Check for conflict code
        if (errorData.code === 'TIME_CONFLICT') {
          console.error('Time conflict detected:', errorData.message);
          
          // Sometimes these are falsely reported due to cancelled appointments
          // Let the BookingPage component handle this with its enhanced error handling
        }
      }
      
      throw error;
    }
  } catch (outerError) {
    console.error('Outer error handler:', outerError);
    throw outerError;
  }
};

// Format date for display
export const formatDate = (date) => {
  return new Date(date).toLocaleDateString('en-AU', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  });
};

// Format time for display
export const formatTime = (date) => {
  return new Date(date).toLocaleTimeString('en-AU', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  });
};

// Format price for display
export const formatPrice = (price) => {
  if (price === null) return 'Price on consultation';
  if (typeof price !== 'number') return `$${price}`;
  return `$${price.toFixed(2)}`;
};

// Initiate payment
export const initiatePayment = async (paymentData) => {
  try {
    const response = await api.post('/api/payments/initiate', paymentData);
    return response.data;
  } catch (error) {
    console.error('Error initiating payment:', error);
    throw error;
  }
};

// Check payment status
export const checkPaymentStatus = async (paymentId, processor, clinicId) => {
  try {
    console.log(`Checking payment status for ID: ${paymentId}, processor: ${processor}, clinic: ${clinicId}`);
    const response = await api.get(`/api/payments/status/${paymentId}`, {
      params: {
        processor,
        clinic_id: clinicId
      }
    });
    console.log('Payment status response:', response.data);
    return response.data;
  } catch (error) {
    console.error('Error checking payment status:', error);
    throw error;
  }
};

// Get payment processor info
export const getPaymentProcessorInfo = (processorType) => {
  return paymentProcessors[processorType.toLowerCase()] || {
    name: processorType,
    logo: null
  };
};